<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";

import SimpleButton from "@/components/ui/SimpleButton.vue";
import SkoreSpinner from "@/components/ui/SkoreSpinner.vue";
import TextInput from "@/components/ui/TextInput.vue";
import { useApi } from "@/services/api";
import { slugify } from "@/services/utils";
import { useAuthStore } from "@/stores/auth";
import type { OnboardingStepEmits } from "@/views/onboarding/types";

const emit = defineEmits<OnboardingStepEmits>();

const api = useApi();
const auth = useAuthStore();

const workspacePublicId = ref<string>("");
const isCreatingWorkspace = ref(false);
const isCheckingWorkspacePublicIdAvailability = ref(false);
const suggestedworkspacePublicId = ref<string | null>(null);
const isWorkspacePublicIdValid = ref(false);
const isWorkspacePublicIdAvailable = ref(false);

const slugifiedWorkspacePublicId = computed(() => slugify(workspacePublicId.value ?? ""));
const isNextEnable = computed(
  () => isWorkspacePublicIdValid.value && isWorkspacePublicIdAvailable.value
);

async function getSuggestedworkspacePublicId() {
  const user = auth.currentUser;

  const naiveSuggestedworkspacePublicId =
    user?.first_name && user.first_name.length > 0 && user.first_name.toLowerCase() !== "none"
      ? `${user.first_name.replace(" ", "-")}-workspace`
      : (user?.email.split("@")[0] ?? "");

  const { available, suggested_slug } = await api.checkworkspacePublicIdAvailability(
    naiveSuggestedworkspacePublicId
  );
  if (!available) {
    workspacePublicId.value = suggested_slug!;
  } else {
    workspacePublicId.value = naiveSuggestedworkspacePublicId;
  }
  isWorkspacePublicIdAvailable.value = true;
}

async function createWorkspace() {
  isCreatingWorkspace.value = true;
  const userId = auth.currentUser?.id ?? "";
  if (userId.length > 0) {
    const workspaceId = await api.createWorkspace(workspacePublicId.value, userId);
    if (workspaceId) {
      emit("continue", { workspaceId });
    }
  }
  isCreatingWorkspace.value = false;
}

async function checkworkspacePublicIdAvailability() {
  if (
    slugifiedWorkspacePublicId.value.length > 0 &&
    slugifiedWorkspacePublicId.value.length <= 64
  ) {
    isCheckingWorkspacePublicIdAvailability.value = true;
    const { available, suggested_slug } = await api.checkworkspacePublicIdAvailability(
      slugifiedWorkspacePublicId.value
    );
    isWorkspacePublicIdAvailable.value = available;
    suggestedworkspacePublicId.value = suggested_slug;
    isCheckingWorkspacePublicIdAvailability.value = false;
  } else {
    // mark invalid name as available
    // to let the user know that the name is invalid
    isWorkspacePublicIdAvailable.value = true;
  }
}

onMounted(async () => {
  await getSuggestedworkspacePublicId();
});

watch(workspacePublicId, () => {
  isWorkspacePublicIdValid.value =
    slugifiedWorkspacePublicId.value.length > 0 && slugifiedWorkspacePublicId.value.length <= 64;
});
</script>

<template>
  <div class="onboarding-create-workspace-step">
    <div class="form">
      <TextInput
        placeholder="Enter your workspace name"
        label="Workspace name"
        v-model="workspacePublicId"
        @keyup.enter="createWorkspace"
        @keyup="checkworkspacePublicIdAvailability"
      />

      <Transition name="fade">
        <div v-if="true" class="hint-container">
          <div class="status-icon">
            <Transition name="fade" mode="out-in">
              <SkoreSpinner v-if="isCheckingWorkspacePublicIdAvailability" />

              <template v-else>
                <div
                  :class="{
                    error: !isWorkspacePublicIdAvailable || !isWorkspacePublicIdValid,
                    success: isWorkspacePublicIdAvailable && isWorkspacePublicIdValid,
                  }"
                >
                  <i
                    class="icon"
                    :class="{
                      'icon-error-circle':
                        !isWorkspacePublicIdAvailable || !isWorkspacePublicIdValid,
                      'icon-success-circle':
                        isWorkspacePublicIdAvailable && isWorkspacePublicIdValid,
                    }"
                  />
                </div>
              </template>
            </Transition>
          </div>

          <div class="hints text-sm-regular">
            <div v-if="!isWorkspacePublicIdAvailable && suggestedworkspacePublicId" class="error">
              This workspace name is not unique, try something else. (like
              <a
                href="#"
                @click="
                  workspacePublicId = suggestedworkspacePublicId;
                  isWorkspacePublicIdAvailable = true;
                "
              >
                {{ suggestedworkspacePublicId }}
              </a>
              )
            </div>

            <div v-else-if="!isWorkspacePublicIdValid" class="error">
              <template v-if="slugifiedWorkspacePublicId.length == 0">
                Workspace name is required.
              </template>

              <template v-else-if="slugifiedWorkspacePublicId.length > 64">
                Workspace name must be less than 64 characters.
              </template>
            </div>

            <div v-else-if="slugifiedWorkspacePublicId != workspacePublicId" class="success">
              Your workspace will be created as <strong>{{ slugifiedWorkspacePublicId }}</strong
              >.
            </div>

            <div v-else class="success">Your workspace name is available.</div>

            <div v-show="!isNextEnable" class="validation-hint">
              The workspace name can only contain ASCII letters, digits, and the characters ., -,
              and _.
            </div>
          </div>
        </div>
      </Transition>
    </div>

    <Transition name="fade" mode="out-in">
      <div v-if="isCreatingWorkspace" class="loader">
        <SkoreSpinner />
      </div>

      <div v-else class="actions">
        <SimpleButton label="Continue" @click="createWorkspace" :disabled="!isNextEnable" />
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.onboarding-create-workspace-step {
  display: flex;
  flex-direction: column;
  gap: var(--numbers-32);

  .form {
    display: flex;
    flex-direction: column;
    gap: var(--numbers-8);

    .error {
      color: var(--content-negative);
    }

    .success {
      color: var(--content-positive);
    }

    a {
      color: inherit;
    }

    .hint-container {
      display: flex;
      flex-direction: row;
      color: var(--content-secondary);
      font-style: italic;
      gap: var(--numbers-4);

      .status-icon {
        display: flex;
        width: var(--numbers-20);
        height: var(--numbers-20);
        align-items: center;
        justify-content: center;

        .icon {
          font-size: var(--numbers-20);
          vertical-align: text-bottom;
        }

        .skore-spinner {
          height: var(--numbers-20);
        }
      }

      .hints {
        strong {
          color: var(--content-inherit);
          font-style: inherit;
        }

        .validation-hint {
          color: var(--content-tertiary);
        }
      }
    }
  }

  .loader {
    display: flex;
    height: var(--numbers-40);
    align-items: center;
    justify-content: center;
  }

  .actions {
    display: flex;
    width: 100%;

    .skore-button {
      flex: 1;
    }
  }
}
</style>
